/*
  Utility to register screen-specific static translations into i18next and
  to retrieve a static translation for a given source text and language.
*/
import changePlayerAvailability from './club/change-player-availability/static.json';
import accountInformation from './components/profile/account-information.json';
import phoneNumberField from './components/profile/phone-number-field.json';
import profileCard from './components/profile/profile-card.json';
import myTransferPoints from './components/profile/my-transfer-points.json';
import clubAdminProfile from './pages/profile/club-admin-profile.json';
import mobileNumberUpdateModal from './components/common/mobile-number-update-modal.json';
import changePasswordModal from './components/common/change-password-modal.json';
// Data feedback ("Report data issue") — button + modal on player/club profiles
// (BUILD-ORDER-data-feedback).
import reportDataIssue from './components/common/report-data-issue.json';
// Club tenders related bundles
import clubTenders from './pages/club/club-tenders.json';
import tenderFilterModal from './components/club/tender-filter-modal.json';
import desktopHeader from './components/club/responsive/desktop-header.json';
import mobileHeader from './components/club/responsive/mobile-header.json';
import mobileTenderTabs from './components/club/responsive/mobile-tender-tabs.json';
import tenderTabs from './components/club/tender-tabs.json';
import listedRequestsListView from './components/common/listed-requests-list-view.json';
import staffTenderCard from './components/club/staff-tender-card.json';
import tenderCard from './components/club/tender-card.json';
import createTender from './pages/club/create-tender.json';
// Club - Request drafts (lifecycle segment, draft card, schedule picker, the
// wizard's three-way finish). Full 16 locale columns, like every bundle the
// completeness test guards.
import requestDraftsBundle from './pages/club/request-drafts.json';
// Club - Request archive (the lifecycle segment's Archive tab, archived badge,
// record, close and reactivate dialogs). Full 16 locale columns, like every
// bundle the completeness test guards. Shared sentences the app already owns
// ("Close request", "Archived", "Chat", "Deal", "Signed", "Accepted pitches",
// "Cancel", "Working...") are deliberately NOT re-registered here.
import requestArchiveBundle from './pages/club/request-archive.json';
// Club - View Pitches
import viewPitchesBundle from './pages/club/view-pitches.json';
// Club - Banned Agents (pitch lifecycle WP4a: the management page, its menu
// entry and the ban/unban endpoint messages). Its "Banned Agents" row is the
// SINGLE source for both the page heading and the header menu item — the
// 'static' namespace is flat and global, so the two cannot drift apart.
import bannedAgentsBundle from './pages/club/banned-agents.json';
// Compare page
import comparePage from './pages/compare/compare.json';
// Discovery
import discoveryBundle from './components/discoveryy/discovery.json';
// Documents
import documentManager from './pages/document/document-manager.json';
// Document - Create Contract
import createContractBundle from './pages/document/create-contract.json';
// Document - Edit Signature
import editSignatureBundle from './pages/document/edit-signature.json';
// Home
import homeBundle from './components/home/home.json';
// Market
import marketBundle from './pages/market/market.json';
// Transfer Windows
import transferWindowsBundle from './pages/transfer-windows/transfer-windows.json';
// Search
import searchBundle from './pages/search/search.json';
// Search - Staff Profile
import staffProfileBundle from './pages/search/staff-profile.json';
// FAQ (rebuild, Ticket 2 Phase B). The old pages/faq/faq.json went away with
// the rebuild; the new /faq surface ships nine bundles instead — one per
// audience tier plus the page chrome — carrying every question, answer, step,
// category label and UI literal of the dataset. 1547 entries, all 16 locale
// columns filled, which is what let them move here out of the staging spot in
// src/constants/faq/i18n/ (an unregistered bundle under src/translations is
// flagged by staticBundleCompleteness, and an incomplete one turns it red).
// src/constants/faq/faqBundleSync.test.js keeps them in lockstep with the
// dataset: every user-facing English string lives in EXACTLY ONE row here.
//
// ONLY THE CHROME BUNDLE IS REGISTERED EAGERLY. Everything imported in this
// file lands in the MAIN chunk, because i18n.js imports this file and every
// page imports i18n.js. The eight TIER bundles (questions, answers, steps) are
// ~3.9 MB of raw JSON and are needed on exactly one route, so they moved to
// pages/faq/faqContentBundles.js and are fetched on demand by
// loadFaqTranslations.js when /faq mounts.
// The chrome bundle stays here on purpose, and the split runs exactly along
// that line, because chrome is the ONLY FAQ bundle that shares English source
// sentences with other screens ("Clear" with tender-filter-modal /
// scout-requests / agent-tenders / invite-player / partners-v2, "Requests"
// with desktop-header / mobile-header / lawyers / roles-and-responsibilities).
// This namespace is flat and global — the bundle registered LAST owns a shared
// sentence app-wide — so deferring chrome would have changed the header's and
// the filter modal's wording the moment somebody opened /faq. The eight tier
// bundles share no sentence with any non-FAQ bundle, which is what makes
// registering them late provably invisible everywhere else.
import faqChrome from './pages/faq/faq-chrome.json';
// Club - My Club
import myClubBundle from './pages/club/my-club.json';
// Club - Invite Staff
import inviteStaffBundle from './pages/club/invite-staff.json';
// Organizers
import organizersBundle from './pages/organizers/organizers.json';
// Lawyers
import lawyersBundle from './pages/lawyer/lawyers.json';
// Meetings
import meetingsBundle from './pages/meetings/meetings.json';
// Club - Roles & Responsibilities
import rolesAndResponsibilities from './pages/club/roles-and-responsibilities.json';
// Transfer News
import transferNewsBundle from './components/transfer-news/transfer-news.json';
// Inbox
import inboxBundle from './components/inbox/inbox.json';
// Inbox - Chat lifecycle timer (Club<->Agent)
import chatTimerBundle from './components/inbox/chat-timer.json';
// Agent - My Squad
import mySquadBundle from './pages/agent/my-squad.json';
// Agent - My Staff (Players|Staff wave)
import myStaffTranslations from './pages/agent/my-staff.json';
// Scout Requests
import scoutRequestsBundle from './pages/scout-requests/scout-requests.json';
// What's New
import whatsNewBundle from './pages/whats-new/whats-new.json';
// Friendlies
import friendliesBundle from './pages/friendlies/friendlies.json';
// Last Minute
import lastMinuteStatic from './pages/last-minute/last-minute.json';
// Last Minute - V2 UI rebuild copy (PROPOSED - English only, pending translation)
import lastMinuteV2 from './pages/last-minute/last-minute-v2.json';
// Team of the Week (TP_TOTW_BRIEF) — page, entry tile, pitch/list/popover copy
import totwBundle from './pages/totw/totw.json';
// Inbox - Threads (message requests, participants rail, handover)
import threadsBundle from './components/inbox/threads.json';
// Agent Tender - request category (two-step subject/level)
import agentTenderRequestCategory from './pages/agent-tender/request-category.json';
// Profile - Agency Admin
import agencyAdminProfile from './pages/profile/agency-admin-profile.json';
// Profile - Agent
import agentProfileBundle from './pages/profile/agent-profile.json';
// Profile - Player
import playerProfileBundle from './pages/profile/player-profile.json';
// Profile - Coach (own page: empty/error states, licence picker options and
// language proficiency options declared in CoachProfile.js)
import coachProfileBundle from './pages/profile/coach-profile.json';
// Profile - Staff (own page: the save/update error banner texts)
import staffProfilePageBundle from './pages/profile/staff-profile.json';
// Profile - Business Card settings
import businessCardSettingsBundle from './pages/profile/business-card-settings.json';
// Profile - Coach page CHILD components (details/licences/languages, playing
// style, defensive tactics, notes, experience, achievements and their modals).
// The pages themselves got their bundles first; these are the components they
// render, which carried their own untranslated sentences.
import coachProfileSectionsBundle from './components/profile/coach-profile-sections.json';
// Profile - Career timeline child components (honours, contributions,
// publications, references, the shared free-text/file blocks) and their modals.
import careerTimelineBundle from './components/profile/career-timeline.json';
// Profile - StaffProfileForm and its section components (club details, work
// permissions, technical skills, licences/trainings, education/languages,
// personality test, coaching philosophy) plus their option datasets.
import staffProfileFormBundle from './components/profile/staff-profile-form.json';
// Agent - Tenders
import agentTendersBundle from './pages/agent/agent-tenders.json';
// Agent - Invite Player
import invitePlayerBundle from './pages/agent/invite-player.json';
// Agent - Invite Staff (TP-Coach-Mandate: invite page + roster/agency chips)
import inviteStaffAgentBundle from './pages/agent/invite-staff.json';
// Auth - Staff mandate public invitee chain (TP-Coach-Mandate)
import staffMandateBundle from './pages/auth/staff-mandate.json';
// Agent - Pitch Player
import pitchPlayerBundle from './pages/agent/pitch-player.json';
// Auth - Sign in/Onboarding/Steps
import authBundle from './pages/auth/auth.json';
// Agency - Player Entries / Add Manual Entry
import agencyPlayerEntries from './pages/agency/agency-player-entries.json';
// Agency - Pitches
import agencyPitchesBundle from './pages/agency/agency-pitches.json';
// Agency - Profile (agents, partners, players, modals)
import agencyProfileBundle from './pages/agency/agency-profile.json';
// Agency - Profile V2 rebuild chrome/shared strings (PROPOSED — EN source
// only; the repo translation pipeline fills the locale columns later,
// exactly like any new string).
import agencyProfileV2Bundle from './pages/agency/agency-profile-v2.json';
// My Agency V2 - Partners surface (wall, add-partner modal, consent moments, confirms)
import partnersV2Bundle from './pages/agency/partners-v2.json';
// Agency - Profile V2 rebuild, invite-agent surface (PROPOSED — EN source)
import agencyInviteV2Bundle from './pages/agency/agency-invite-v2.json';
// Agency - Profile V2 rebuild, Agents-tab surface strings (PROPOSED — EN
// source only, artboards 1a-1g/1k/1l/1m/1n/2g).
import agencyAgentsV2Bundle from './pages/agency/agency-agents-v2.json';
// Agency - Taskboard
import agencyTaskboardBundle from './pages/agency/agency-taskboard.json';
// Contacts
import addContactModal from './components/contacts/add-contact-modal.json';
// Club-Network (N2/N3) — English source strings registered per convention
// (DoD 25). Only the EN source column is authored here; the repo translation
// pipeline fills the 17 language columns later, exactly like any new string.
import clubNetwork from './components/club-network/club-network.json';
// Commissioned Search (QCS Redesign 4.0, Ticket 9 Part B) — every new
// user-facing FE string of the feature (club section-home / cards / confirms,
// create-flow A10-A12, agent inbox B1/B4/B5/B8/B10/B11, deliver flow B3/A3,
// club delivery view A2/A3), fully translated into all 16 locales. Shared
// sentences the app already owns (Cancel, Chat, Accepted, Declined, Close
// request, My search requests, …) are deliberately NOT re-registered here.
// The two resolution lines (request.resolution_line / recipient.row_resolution)
// are registered as {winner} templates so the fuzzy matcher composes them from
// the BE-rendered sentence — no BE/FE change (Part B §6).
import commissionedSearch from './pages/club-network/commissioned-search.json';
// Club-Network — Redesign 2.0 UI rebuild copy (PROPOSED - English only, pending translation)
import clubNetworkV2 from './components/club-network/club-network-v2.json';
// Club Radar V2 — R5 mobile swipe-row strings (PROPOSED - English only, pending translation)
import clubRadarV2Swipe from './pages/club/club-radar-v2-swipe.json';
import contactCard from './components/contacts/contact-card.json';
import deleteConfirmationModalContacts from './components/contacts/delete-confirmation-modal.json';
// Agent Tenders (Phase 1 + 1.5): feed, card, filter, my-requests + tabs,
// responses, verify-now, send-player, create. Public verify page separate.
import agentTenderStatic from './pages/agent-tender/agent-tender.json';
import agentTenderPublicStatic from './pages/agent-tender/agent-tender-public.json';
// EN-source-only additions (P3 audit): agent-request strings not yet in the
// bundles above. TEXT = exact English source; locale columns are intentionally
// omitted so the runtime falls back to English until the repo translation
// pipeline fills them (team convention, not hand-authored translations).
import agentTenderFeAdditions from './pages/agent-tender/agent-tender-fe-additions.json';
// Agent Request V2 card/feed/loupe strings (design doc 01, approved
// 2026-07-21). EN source only — the repo translation pipeline fills the
// locale columns later, exactly like any new string.
import agentTenderV2Card from './pages/agent-tender/agent-tender-v2-card.json';
// Agent Requests V2 — create form + post-success step (EN source only; the
// repo translation pipeline fills the locale columns later, per convention).
import agentTenderV2Create from './pages/agent-tender/agent-tender-v2-create.json';
// Agent Request V2 verify surfaces (EN source; translations via the pipeline)
import agentTenderV2Verify from './pages/agent-tender/agent-tender-v2-verify.json';
// Agent Request V2 responses inbox restyle (design doc 03 screen 7b; EN
// source only — the repo translation pipeline fills the locale columns later).
import agentTenderV2Responses from './pages/agent-tender/agent-tender-v2-responses.json';
// V2 additive bundles (hardcoded-UI extraction audit): strings previously
// hardcoded in components, extracted to bundles so all 16 locale columns apply.
import playerStatsLabels from './components/search/player-stats-labels.json';
// Match Lineup component (player-profile Matches tab, TP-Lineup build)
import matchLineupBundle from './components/search/match-lineup.json';
import commonHardcodedAdditions from './components/common/common-hardcoded-additions.json';
import profileOptionDatasets from './pages/profile/profile-option-datasets.json';
import homeHardcodedAdditions from './components/home/home-hardcoded-additions.json';
import inboxHardcodedAdditions from './components/inbox/inbox-hardcoded-additions.json';
import profileHardcodedAdditions from './pages/profile/profile-hardcoded-additions.json';
import requestTerminologyAdditions from './components/common/request-terminology-additions.json';
import representationTerminologyAdditions from './components/common/representation-terminology-additions.json';
// DESIGN_007: the read-only "How email delivery works" hint on the
// notification settings page (quiet hours, pitch digest, default-off events).
import notificationSettingsBundle from './pages/settings/notification-settings.json';
// Notifications hub (/notifications): search, category filter chips and the
// narrowed empty state (hub coverage extension 2026-08). Full 16 locale
// columns, like every bundle the completeness test guards.
import notificationsHubBundle from './pages/notifications/notifications-hub.json';
// Coaching Personality (catalogue v1): the questionnaire and its evaluation.
// Source of truth for every value is TP_COACH_CHARACTER_KATALOG_V1.md (EN/DE)
// plus TP_COACH_CHARACTER_TRANSLATIONS.md / _2.md (the other 14 locales). Each
// row's `context` carries the catalogue ID(s) it serves, so the backend can map
// `stmt.1.1`, `level.3.high.text` … onto the English sentence that is the key.
import coachingPersonalityBundle from './pages/profile/coaching-personality.json';
// Coaching Personality (WP2 surface): the handful of sentences the SURFACE
// needs that the catalogue does not define — the private development
// comparison decided in WP2, and the five Big Five subtitles the accepted
// prototype prints under each axis name (Konzept chapter 2). EN source only,
// per the repo convention for new copy; the locale columns arrive with Marin's
// next translation batch and are flagged as pending in the build report.
import coachingPersonalityUiBundle from './pages/profile/coaching-personality-ui.json';
// FIFA Verified badge (D-A): wordmark + tooltip. DELIBERATELY the identical
// English value in every locale column — the badge is fixed English in ALL
// languages (Marin 2026-08-04), so this bundle pins the sentences instead of
// letting the runtime machine-translate them.
import fifaVerifiedBadgeBundle from './components/common/fifa-verified-badge.json';
// InfoDot page explainer (Ticket 2): the reusable "i" dot next to page
// titles. Carries the "Learn more" footer-link label (arrow rendered
// separately, aria-hidden) and the trigger's default aria-label.
import infoDotBundle from './components/common/info-dot.json';
// InfoDot rollout copy (Ticket 3): the per-page popover body texts (Agent
// Requests feed, agent search-request inbox). The page TITLES are already
// bundled elsewhere (agent-tender.json / club-network.json) and are NOT
// re-registered here.
import infoDotCopyBundle from './components/common/info-dot-copy.json';
// Competitions embeddables (standings/groups/bracket): table header Kuerzel,
// zone-legend/bracket chips and templates. K10-verbatim German; shared
// sentences other bundles already own (Team, Form, Champions League, Europa
// League, Relegation, Final) are deliberately NOT re-registered here.
import competitionsStandingsBundle from './components/competitions/competitions-standings.json';
// Player photo review flow: the "Change profile photo" entry in the player
// edit dropdown (agent MySquad + club MyPlayers), its success/error toasts and
// the 24h review notice. Full 16 locale columns.
import playerPhotoUploadBundle from './components/common/player-photo-upload.json';
// Staff/Coach requests page — Applied tab (self-applications). Only the
// sentences NEW to that surface; the shared ones ("Applied", "Accepted",
// "Rejected", "Expired", "Open chat", "Show message", "Hide message", ...)
// are already registered by other bundles in this flat namespace and are
// deliberately NOT re-registered here.
import staffAppliedBundle from './pages/staff/staff-applied.json';
// Player profile — Attributes tab (provider ability values). Registers at the
// very END of the bundles array on purpose: its full 16-column rows carry the
// design-handoff German and must win the last-wins merge over three earlier
// partial collisions — "Attributes" (last-minute.json, DE "Merkmale"),
// "Interceptions" (player-stats-labels.json, DE "Abfangaktionen") and
// "Strength" (profile-option-datasets.json, DE "Kraft"). "Retry" and the
// stat labels whose German already matches the handoff (Attacking, Defending,
// Dribbling, Finishing, Pace, Passing, Physical, Shooting) are deliberately
// NOT re-registered here — they stay owned by their existing bundles.
import playerAttributesBundle from './components/search/player-attributes.json';
// Competitions — embeddable Schedule + Stats leaderboards (UI-SPEC K1/K9/K10).
// Shared sentences the app already owns ("Goals", "Assists", "Key passes",
// "Duels won", "Big chances created", "Tackles", "Interceptions", "Saves",
// "Player", "Min", "1st leg", "2nd leg", "Final", "Round {round}") are
// deliberately NOT re-registered here.
import competitionsScheduleStatsBundle from './components/competitions/competitions-schedule-stats.json';
// Competitions (TP-CompetitionPage build): browse page, competition-page
// shell (hero/nav/overview) and the mega-menu entry. Full 16 locale columns.
// Registers LAST (after playerAttributesBundle) ON PURPOSE: UI-SPEC K10
// demands the handoff German verbatim, so two earlier collisions are
// deliberately overridden last-wins — "Overview" (compare.json DE
// "Überblick" → "Übersicht") and "Schedule" (request-drafts.json DE
// "Terminieren" → "Spielplan"). Both app-wide changes are flagged in the
// competitions PR for Marin.
import competitionsBundle from './pages/competitions/competitions.json';
// Player profile — Matches tab, lineup bench/substitution/unavailable strings
// (design-handoff German proven in board L11: "Ersatzbank", "Gesperrt",
// "Fraglich", …). "Coach" is deliberately NOT re-registered — scout-requests
// and search bundles already carry it with the matching DE "Trainer".
import matchLineupBenchBundle from './components/player/match-lineup-bench.json';

// Map from provided language labels to i18n language codes
const NAME_TO_CODE = {
  'SPANISH': 'es',
  'FRENCH': 'fr',
  'GERMAN': 'de',
  'ITALIAN': 'it',
  'PORTUGUESE': 'pt',
  'PORTUGUESE(BRAZIL)': 'pt-BR',
  'PORTUGUESE(PORTUGAL)': 'pt-PT',
  'RUSSIAN': 'ru',
  'JAPANESE': 'ja',
  'ARABIC': 'ar',
  'KOREAN': 'ko',
  'CHINESE(SIMPLIFIED)': 'zh',
  'CHINESE(TRADITIONAL)': 'zh-TW',
  'DANISH': 'da',
  'TURKISH': 'tr',
  'CROATIAN': 'hr',
  'CROATIANN': 'hr', // robustness for possible typos
  'CROATIANN?': 'hr'
};

// Normalize language codes from backend/current UI to i18n codes
export const normalizeLang = (code) => {
  const c = (code || '').toLowerCase();
  if (!c) return 'en';
  if (c === 'zh-hans' || c === 'zh-cn' || c === 'zh') return 'zh';
  if (c === 'zh-hant' || c === 'zh-tw') return 'zh-TW';
  if (c === 'pt-br') return 'pt-BR';
  if (c === 'pt-pt') return 'pt-PT';
  return c;
};

// Build resources object for a given items array
const buildResourcesFromItems = (items) => {
  const resources = {};
  items.forEach((entry) => {
    const textKey = typeof entry.TEXT === 'string' ? entry.TEXT.trim() : '';
    const structuredKey = typeof entry.key === 'string' ? entry.key.trim() : '';
    const englishText = typeof entry.text === 'string' ? entry.text.trim() : (textKey || '');
    if (!textKey && !structuredKey && !englishText) return;

    // Generate normalized variants to improve matching reliability across UI strings
    const normalizeAscii = (s) => (s || '')
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'")
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const stripNumberPrefix = (s) => (s || '').replace(/^\s*\d+\.\s*/, '');
    const stripTrailingQuotes = (s) => (s || '').replace(/['’"]+$/, '');
    const toArrowRight = (s) => (s || '').replace(/[➔]/g, '→');
    const toArrowHook = (s) => (s || '').replace(/[→]/g, '➔');
    const generateKeyVariants = (s) => {
      if (!s) return [];
      const variants = new Set();
      const base = (s || '').trim();
      const add = (v) => { if (v && v.trim()) variants.add(v.trim()); };
      add(base);
      add(stripNumberPrefix(base));
      add(stripTrailingQuotes(base));
      add(toArrowRight(base));
      add(toArrowHook(base));
      add(normalizeAscii(base));
      add(normalizeAscii(stripNumberPrefix(base)));
      add(normalizeAscii(stripTrailingQuotes(base)));
      add(normalizeAscii(toArrowRight(base)));
      add(normalizeAscii(toArrowHook(base)));
      return Array.from(variants).filter(Boolean);
    };

    const keysToRegister = Array.from(new Set([
      structuredKey,
      ...generateKeyVariants(textKey),
      ...generateKeyVariants(englishText)
    ].filter(Boolean)));

    // English base for all keys we will register
    if (keysToRegister.length > 0) {
      resources['en'] = resources['en'] || { static: {} };
      keysToRegister.forEach((k) => {
        resources['en'].static[k] = englishText || k;
      });
    }

    // Other locales
    Object.keys(entry).forEach((k) => {
      const upper = k.toUpperCase();
      const code = NAME_TO_CODE[upper];
      if (!code) return;
      const value = entry[k];
      if (!value) return;
      if (!resources[code]) resources[code] = { static: {} };
      keysToRegister.forEach((rk) => {
        resources[code].static[rk] = value;
      });
    });
  });
  return resources;
};

// Store placeholder-based static patterns for template matching
const STATIC_TEMPLATE_PATTERNS = [];
const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const extractPlaceholders = (key) => {
  const names = [];
  key.replace(/\{(\w+)\}/g, (_, name) => {
    names.push(name);
    return '';
  });
  return names;
};
const buildRegexForKey = (key) => {
  // Replace placeholders with a non-greedy capture group
  const pattern = '^' + escapeRegex(key).replace(/\\\{(\w+)\\\}/g, '"?(.+?)"?') + '$';
  return new RegExp(pattern);
};

// ── Template-pattern hygiene ──────────────────────────────────────────────
// A template key becomes a regex where every {placeholder} turns into a
// wildcard. What is left over — the literal text — is the only thing that
// makes the pattern selective AND the only thing that can actually be
// translated (placeholders are filled with runtime values verbatim).
//
// Everything below is derived from that single observation.

// The literal remainder of a template key, i.e. the key minus its placeholders.
const templateLiteralText = (key) => String(key || '').replace(/\{\w+\}/g, '');

// Characters that carry no translatable information on their own: whitespace
// plus the punctuation/separators that survive in template scaffolding
// ("{a} - {b}", "{a} · {b}", "{a}: {b}", CJK/full-width variants).
// Deliberately written without Unicode property escapes so it needs no
// transpiler support; anything not listed counts as informative, so an
// unlisted exotic separator only means the key keeps today's behaviour.
// eslint-disable-next-line no-useless-escape
const NON_INFORMATIVE_ONLY = /^[\s\u00A0\u00A1\u00AB\u00B0\u00B7\u00BB\u00BF\u00D7!-\/:-@[-`{-~\u2000-\u206F\u2E00-\u2E7F\u3000-\u303F\uFF01-\uFF0F\uFF1A-\uFF20]*$/;

/**
 * True when a key consists exclusively of placeholders, whitespace and
 * punctuation — e.g. "{days} {type}".
 *
 * Such a key must never become a match pattern:
 *  - it cannot translate anything, because the whole rendered sentence is
 *    made of substituted runtime values, and
 *  - its regex degenerates to `^"?(.+?)"? "?(.+?)"?$`, which matches virtually
 *    every string and would swallow every pattern registered after it.
 *
 * Exact lookups via getStaticTranslation are untouched by this — only the
 * fuzzy template-matching layer skips these keys.
 */
export const isPlaceholderOnlyKey = (key) => {
  const raw = String(key || '');
  if (!/\{\w+\}/.test(raw)) return false;
  return NON_INFORMATIVE_ONLY.test(templateLiteralText(raw));
};

/**
 * How much literal (non-whitespace) text a template key carries. Used to order
 * patterns from specific to general, so that "Expires in {n} days" is always
 * tried before "{n} days" no matter which bundle happened to register first.
 */
export const templateLiteralWeight = (key) =>
  templateLiteralText(key).replace(/\s+/g, '').length;

// Order patterns by descending literal weight; ties keep registration order
// (each pattern carries the monotonic `order` it was registered with, so the
// result is stable even if this runs more than once).
const sortTemplatePatternsBySpecificity = (patterns) => {
  patterns.sort((a, b) =>
    (templateLiteralWeight(b.key) - templateLiteralWeight(a.key)) || (a.order - b.order));
  return patterns;
};

/**
 * Build the ordered template-pattern list for a set of bundle item arrays.
 * This is the single place that decides which keys become match patterns and
 * in which order they are tried; registerStaticTranslations feeds the real
 * bundles through it, tests feed controlled ones.
 */
export const buildTemplatePatterns = (bundles) => {
  const patterns = [];
  (bundles || []).forEach((items) => {
    (items || []).forEach((entry) => {
      const candidates = [];
      if (typeof entry.TEXT === 'string') candidates.push(entry.TEXT.trim());
      if (typeof entry.text === 'string') candidates.push(entry.text.trim());
      candidates.forEach((key) => {
        if (!key) return;
        if (!(key.includes('{') && key.includes('}'))) return;
        // A key made of nothing but placeholders (e.g. "{days} {type}") has no
        // literal text: it cannot translate anything and its regex would match
        // almost every sentence, hiding every pattern behind it. Skip it as a
        // *pattern*; the exact lookup in getStaticTranslation still serves it,
        // and the bundle entry itself stays untouched.
        if (isPlaceholderOnlyKey(key)) return;
        try {
          patterns.push({
            key,
            placeholders: extractPlaceholders(key),
            regex: buildRegexForKey(key),
            order: patterns.length
          });
        } catch (_) {}
      });
    });
  });
  return sortTemplatePatternsBySpecificity(patterns);
};

// Read-only view of the live pattern list (diagnostics/tests).
export const getStaticTemplatePatterns = () => STATIC_TEMPLATE_PATTERNS.slice();

// Register all screen bundles
// (mega-menu labels added to agentTenderStatic — DoD 22)
export const registerStaticTranslations = (i18nInstance) => {
  const bundles = [
    changePlayerAvailability,
    accountInformation,
    phoneNumberField,
    profileCard,
    myTransferPoints,
    clubAdminProfile,
    mobileNumberUpdateModal,
    changePasswordModal,
    reportDataIssue,
    // Club tenders domain
    clubTenders,
    tenderFilterModal,
    desktopHeader,
    mobileHeader,
    mobileTenderTabs,
    tenderTabs,
    listedRequestsListView,
    staffTenderCard,
    tenderCard,
    createTender,
    requestDraftsBundle,
    requestArchiveBundle,
    viewPitchesBundle,
    bannedAgentsBundle,
    discoveryBundle,
    homeBundle,
    marketBundle,
    transferWindowsBundle,
    searchBundle,
    staffProfileBundle,
    myClubBundle,
    inviteStaffBundle,
    organizersBundle,
    lawyersBundle,
    meetingsBundle,
    rolesAndResponsibilities,
    transferNewsBundle,
    inboxBundle,
    chatTimerBundle,
    mySquadBundle,
    myStaffTranslations,
    scoutRequestsBundle,
    whatsNewBundle,
    agencyAdminProfile,
    agentProfileBundle,
    playerProfileBundle,
    coachProfileBundle,
    staffProfilePageBundle,
    businessCardSettingsBundle,
    coachProfileSectionsBundle,
    careerTimelineBundle,
    staffProfileFormBundle,
    agentTendersBundle,
    invitePlayerBundle,
    inviteStaffAgentBundle,
    staffMandateBundle,
    pitchPlayerBundle,
    authBundle,
    agencyPlayerEntries,
    agencyPitchesBundle,
    agencyProfileBundle,
    agencyProfileV2Bundle,
    partnersV2Bundle,
    agencyInviteV2Bundle,
    agencyAgentsV2Bundle,
    agencyTaskboardBundle,
    createContractBundle,
    editSignatureBundle,
    documentManager,
    comparePage,
    addContactModal,
    contactCard,
    deleteConfirmationModalContacts,
    agentTenderStatic,
    agentTenderPublicStatic,
    agentTenderFeAdditions,
    agentTenderV2Card,
    agentTenderV2Create,
    agentTenderV2Verify,
    agentTenderV2Responses,
    agentTenderRequestCategory,
    clubNetwork,
    commissionedSearch,
    clubNetworkV2,
    clubRadarV2Swipe,
    friendliesBundle,
    lastMinuteStatic,
    lastMinuteV2,
    totwBundle,
    // V2 additive bundles (hardcoded-UI extraction audit)
    playerStatsLabels,
    matchLineupBundle,
    fifaVerifiedBadgeBundle,
    infoDotBundle,
    infoDotCopyBundle,
    commonHardcodedAdditions,
    profileOptionDatasets,
    homeHardcodedAdditions,
    inboxHardcodedAdditions,
    profileHardcodedAdditions,
    requestTerminologyAdditions,
    representationTerminologyAdditions,
    notificationSettingsBundle,
    notificationsHubBundle,
    coachingPersonalityBundle,
    coachingPersonalityUiBundle,
    playerPhotoUploadBundle,
    competitionsStandingsBundle,
    staffAppliedBundle,
    matchLineupBenchBundle,
    competitionsScheduleStatsBundle,
    // FAQ — the page chrome only. The eight tier bundles are fetched on demand
    // (loadFaqTranslations.js); see the import comment above for why the line
    // runs exactly here.
    faqChrome,
    // threadsBundle registers LAST so its full 16-column entries win the
    // merge over earlier partial entries (e.g. lawyers.json es/fr/de-only).
    threadsBundle,
    // playerAttributesBundle registers after threadsBundle (no shared keys):
    // its rows must win over the three earlier collisions named at its import.
    playerAttributesBundle,
    // competitionsBundle registers LAST: its full 16-column rows must win the
    // two named collisions ("Overview", "Schedule") — see its import comment.
    competitionsBundle
  ];
  registerBundles(i18nInstance, bundles);
};

/**
 * Merge a set of bundles into the flat `static` namespace and extend the
 * template-pattern list with theirs.
 *
 * Split out of registerStaticTranslations so the on-demand FAQ loader walks
 * the SAME code path as the eager registration instead of a copy of it — the
 * key-variant generation, the merge semantics ("registered last wins") and the
 * specificity sort of the fuzzy matcher all live here, once.
 *
 * Calling it more than once is by design: patterns are appended with a
 * monotonic order offset and re-sorted, so a bundle set that arrives later
 * takes its correct place in the match order rather than the end of the queue.
 */
export const registerBundles = (i18nInstance, bundles) => {
  (bundles || []).forEach((items) => {
    const resources = buildResourcesFromItems(items);

    Object.keys(resources).forEach((lng) => {
      const bundle = resources[lng].static;
      // Merge into the shared 'static' namespace
      const hasExisting = i18nInstance.hasResourceBundle(lng, 'static');
      if (hasExisting) {
        const existing = i18nInstance.getResourceBundle(lng, 'static');
        i18nInstance.addResourceBundle(lng, 'static', { ...existing, ...bundle }, true, true);
      } else {
        i18nInstance.addResourceBundle(lng, 'static', bundle, true, true);
      }
    });
  });

  // Template patterns for the fuzzy matcher. Match order must not depend on
  // the accident of bundle order: the list is kept most-specific-first (most
  // literal text), so a broad template can never silently capture a sentence
  // that a narrower one owns.
  const base = STATIC_TEMPLATE_PATTERNS.length;
  buildTemplatePatterns(bundles || []).forEach((pattern) => {
    STATIC_TEMPLATE_PATTERNS.push({ ...pattern, order: base + pattern.order });
  });
  sortTemplatePatternsBySpecificity(STATIC_TEMPLATE_PATTERNS);
};

// Lookup helper used by TranslationContext
export const getStaticTranslation = (i18nInstance, text, langCode) => {
  const key = (text || '').trim();
  if (!key) return null;
  const lng = normalizeLang(langCode);
  // If key exists in 'static' namespace for this lng, return it; else null
  const res = i18nInstance.getResource(lng, 'static', key);
  if (typeof res === 'undefined' || res === null) return null;
  const translated = i18nInstance.t(key, { lng, ns: 'static', defaultValue: res });
  // If translation equals key and lng is not 'en', still treat as static if explicitly provided
  if (!translated) return null;
  return translated;
};

// Attempt to match dynamic sentences with placeholders defined in static keys
export const matchTemplatePatterns = (patterns, i18nInstance, text, langCode) => {
  const candidate = (text || '').trim();
  if (!candidate) return null;
  const lng = normalizeLang(langCode);
  for (const pattern of patterns) {
    const match = candidate.match(pattern.regex);
    if (match) {
      // The target language must actually own this template. i18next is
      // configured with fallbackLng 'en', so t() happily returns the ENGLISH
      // source for a key whose locale column is missing. That counts as a
      // static hit, and TranslationContext then never calls the translation
      // service — the sentence is pinned to English by the very layer that
      // was supposed to translate it. Same failure as "{days} {type}",
      // reached from the other side. getStaticTranslation guards its own
      // lookup exactly like this; the fuzzy layer did not.
      //
      // This matters because the team convention is to land new bundles with
      // the EN source column only and let the translation pipeline fill the
      // locales later. Such an entry must fall through to the service, not
      // silently win.
      const own = i18nInstance.getResource(lng, 'static', pattern.key);
      if (typeof own === 'undefined' || own === null || own === '') continue;
      // Fetch the translated template
      const template = i18nInstance.t(pattern.key, { lng, ns: 'static', defaultValue: own });
      if (!template) continue;
      // Replace placeholders in the template with captured groups
      let out = template;
      pattern.placeholders.forEach((name, idx) => {
        const value = (match[idx + 1] || '').trim();
        out = out.replace(new RegExp(`\\{${name}\\}`, 'g'), value);
      });
      return out;
    }
  }
  return null;
};

// Live matcher used by TranslationContext.
export const getStaticTemplateMatch = (i18nInstance, text, langCode) =>
  matchTemplatePatterns(STATIC_TEMPLATE_PATTERNS, i18nInstance, text, langCode);


